import React, { useState } from 'react';
import { FaFileUpload, FaBuilding, FaCode, FaCheckCircle } from 'react-icons/fa';
import { GoogleGenerativeAI } from '@google/generative-ai';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';

// First, set the pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ResumeChecker = () => {
  const [pdfText, setPdfText] = useState('');
  const [formData, setFormData] = useState({
    role: '',
    company: '',
    skills: []
  });
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const companies = [
    'Google', 'Microsoft', 'Amazon', 'Apple', 'Meta',
    'Netflix', 'IBM', 'Oracle', 'Intel', 'Adobe'
  ];

  const roles = [
    'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
    'DevOps Engineer', 'Data Scientist', 'UI/UX Designer',
    'Product Manager', 'Software Engineer', 'Mobile Developer'
  ];

  const skillsList = [
    'JavaScript', 'Python', 'Java', 'React', 'Node.js',
    'AWS', 'Docker', 'Kubernetes', 'SQL', 'MongoDB'
  ];

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += pageText + '\n';
        }

        setPdfText(fullText);
        
        // Validate extracted text
        if (!fullText.trim()) {
          throw new Error('No text could be extracted from the PDF');
        }

        // Basic content validation
        const sections = ['experience', 'education', 'skills', 'projects'];
        const hasCommonSections = sections.some(section => 
          fullText.toLowerCase().includes(section)
        );

        if (!hasCommonSections) {
          alert('Warning: The PDF might not be properly formatted. Please ensure it contains standard resume sections.');
        }

      } catch (error) {
        console.error('Error reading PDF:', error);
        alert('Error reading PDF. Please ensure the file is not corrupted and is properly formatted.');
        setPdfText('');
      }
    } else {
      alert('Please upload a valid PDF file');
    }
  };

  const analyzeResume = async () => {
    setLoading(true);
    try {
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: 'gemini-1.5-pro' });
  
      const prompt = `Analyze this resume for a ${formData.role} position at ${formData.company}. 
      The candidate is skilled in: ${formData.skills.join(', ')}
      
      Resume Content:
      ${pdfText}
      
      Provide a JSON object with the following structure:
      {
        "atsScore": number between 0-100,
        "strengths": array of strings,
        "improvements": array of strings,
        "suggestedCompanies": array of strings,
        "tips": array of strings,
        "recommendedWebsites": array of strings
      }
      
      Return ONLY the JSON object with no additional text, markdown formatting, or code blocks.`;
  
      const result = await model.generateContent(prompt);
      const response = await result.response.text();
      
      // Clean the response by removing markdown formatting
      const cleanResponse = response
        .replace(/```json/g, '')
        .replace(/```/g, '')
        .trim();
  
      // Find the JSON object in the response
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON object found in response');
      }
  
      const parsedResponse = JSON.parse(jsonMatch[0]);
      setAnalysis(parsedResponse);
    } catch (error) {
      console.error('Error analyzing resume:', error);
      // Add user-friendly error message
      alert('Failed to analyze resume. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8">
        <div className="flex items-center space-x-3 mb-8">
          <FaFileUpload className="text-2xl text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">AI Resume Checker</h2>
        </div>

        {/* File Upload */}
        <div className="mb-8">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Upload Resume (PDF)
          </label>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
                     file:rounded-full file:border-0 file:text-sm file:font-semibold
                     file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
        </div>

        {/* Role Selection */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Target Role
          </label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                     rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select Role</option>
            {roles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>

        {/* Company Selection */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Target Company
          </label>
          <select
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                     rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select Company</option>
            {companies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </div>

        {/* Skills Selection */}
        <div className="mb-8">
          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Key Skills
          </label>
          <div className="flex flex-wrap gap-2">
            {skillsList.map(skill => (
              <button
                key={skill}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    skills: prev.skills.includes(skill)
                      ? prev.skills.filter(s => s !== skill)
                      : [...prev.skills, skill]
                  }));
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                          ${formData.skills.includes(skill)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={analyzeResume}
          disabled={!pdfText || !formData.role || !formData.company || formData.skills.length === 0}
          className="w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold
                   hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Analyzing...' : 'Analyze Resume'}
        </button>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Analysis Results</h3>
            <div className="flex items-center space-x-2">
              <FaCheckCircle className="text-green-500" />
              <span className="text-2xl font-bold text-green-500">{analysis.atsScore}/100</span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Strengths */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Strengths</h4>
              <ul className="list-disc pl-5 space-y-1">
                {analysis.strengths.map((strength, index) => (
                  <li key={index} className="text-gray-600 dark:text-gray-400">{strength}</li>
                ))}
              </ul>
            </div>

            {/* Improvements */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Areas for Improvement</h4>
              <ul className="list-disc pl-5 space-y-1">
                {analysis.improvements.map((improvement, index) => (
                  <li key={index} className="text-gray-600 dark:text-gray-400">{improvement}</li>
                ))}
              </ul>
            </div>

            {/* Suggested Companies */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Recommended Companies</h4>
              <div className="flex flex-wrap gap-2">
                {analysis.suggestedCompanies.map((company, index) => (
                  <span key={index} className="px-3 py-1 bg-green-50 dark:bg-green-900/20 
                                             text-green-700 dark:text-green-300 rounded-full text-sm">
                    {company}
                  </span>
                ))}
              </div>
            </div>

            {/* Resume Building Tips */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Resume Tips</h4>
              <ul className="list-disc pl-5 space-y-1">
                {analysis.tips.map((tip, index) => (
                  <li key={index} className="text-gray-600 dark:text-gray-400">{tip}</li>
                ))}
              </ul>
            </div>

            {/* Recommended Websites */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Recommended Resume Building Websites</h4>
              <ul className="list-disc pl-5 space-y-1">
                {analysis.recommendedWebsites.map((website, index) => (
                  <li key={index}>
                    <a href={website} target="_blank" rel="noopener noreferrer"
                       className="text-indigo-600 dark:text-indigo-400 hover:underline">
                      {website}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeChecker;